<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Avatar from '@/components/Avatar.vue'
import Button from '@/components/Button.vue'
import Modal from '@/components/Modal.vue'
import Select from '@/components/Form/Select.vue'
import { useAlert } from '@/composables/useToast'
import { sendAgentNotice } from '@/api/agentNotices'
import { getLostThreads, getMonitoring } from '@/api/monitoring'
import { getInboxes } from '@/api/inboxes'
import { getAgents, setAgentAvailability } from '@/api/agents'
import { getTags } from '@/api/tags'

const REFRESH_INTERVAL = 15000
let refreshTimer = null

const store = useStore()
const router = useRouter()
const { t } = useI18n()

const currentUser = computed(() => store.getters['auth/getCurrentUser'])
const accountId = computed(() => currentUser.value?.accounts?.[0]?.id)

// Supervisor takes a stuck operator off the line right from the table
const changeAgentAvailability = async (agent, availability) => {
  if (availability === agent.availability) return
  try {
    await setAgentAvailability(accountId.value, agent.id, availability)
    await loadReport()
  } catch (error) {
    useAlert(t('AVAILABILITY.ERROR'))
  }
}

const report = ref(null)
const loading = ref(false)
const updatedAt = ref(null)

const inboxes = ref([])
const agents = ref([])
const tags = ref([])

const filterInboxId = ref('')
const filterAgentId = ref('')
const filterTagId = ref('')
const filterSince = ref('')
const filterUntil = ref('')

const loadReport = async () => {
  if (!accountId.value) return
  loading.value = true
  try {
    const { data } = await getMonitoring(accountId.value, {
      inbox_id: filterInboxId.value || undefined,
      agent_id: filterAgentId.value || undefined,
      tag_id: filterTagId.value || undefined,
      since: filterSince.value || undefined,
      until: filterUntil.value || undefined,
    })
    report.value = data
    updatedAt.value = new Date()
  } finally {
    loading.value = false
  }
}

const toOptions = (records, emptyLabel) => [
  { value: '', label: emptyLabel },
  ...records.map(record => ({ value: record.id, label: record.name })),
]

const inboxOptions = computed(() =>
  toOptions(inboxes.value, t('MONITORING.FILTERS.ANY_INBOX'))
)
const agentOptions = computed(() =>
  toOptions(agents.value, t('MONITORING.FILTERS.ANY_AGENT'))
)
const tagOptions = computed(() =>
  toOptions(tags.value, t('MONITORING.FILTERS.ANY_TAG'))
)

const loadFilterOptions = async () => {
  if (!accountId.value) return
  const [inboxesResponse, agentsResponse, tagsResponse] = await Promise.all([
    getInboxes(accountId.value),
    getAgents(accountId.value),
    getTags(accountId.value),
  ])
  inboxes.value = inboxesResponse.data
  agents.value = agentsResponse.data
  tags.value = tagsResponse.data
}

watch([filterInboxId, filterAgentId, filterTagId, filterSince, filterUntil], () =>
  loadReport()
)

const counters = computed(() => {
  const values = report.value?.counters
  if (!values) return []
  return [
    { key: 'queue', value: values.queue, icon: 'i-lucide-clock' },
    { key: 'active', value: values.active, icon: 'i-lucide-message-circle' },
    {
      key: 'waiting_agent',
      value: values.waiting_agent,
      icon: 'i-lucide-user-round',
    },
    {
      key: 'waiting_client',
      value: values.waiting_client,
      icon: 'i-lucide-hourglass',
    },
    { key: 'resolved', value: values.resolved, icon: 'i-lucide-check' },
    {
      key: 'stuck',
      value: values.stuck,
      icon: 'i-lucide-alert-triangle',
      alert: values.stuck > 0,
    },
    // Only worth a tile once the bot actually works in some source
    ...(values.bot_answered
      ? [
          {
            key: 'bot_answered',
            value: values.bot_answered,
            icon: 'i-lucide-sparkles',
          },
          {
            key: 'bot_handed_over',
            value: values.bot_handed_over,
            icon: 'i-lucide-user-round-plus',
          },
        ]
      : []),
  ]
})

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return '—'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 1) return `${Math.round(seconds)} ${t('MONITORING.SECONDS')}`
  const hours = Math.floor(minutes / 60)
  if (hours < 1) return `${minutes} ${t('MONITORING.MINUTES')}`
  return `${hours}:${String(minutes % 60).padStart(2, '0')}`
}

const timeFormatter = new Intl.DateTimeFormat('ru', {
  hour: '2-digit',
  minute: '2-digit',
})
const formatWaitingSince = value => timeFormatter.format(new Date(value))

const updatedFormatter = new Intl.DateTimeFormat('ru', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

const durationSince = startedAt => {
  if (!startedAt) return '—'
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000)
  )
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
}

// Supervisors look for people on the line first, so they head the table
const AVAILABILITY_ORDER = ['online', 'busy', 'offline']

const sortedAgents = computed(() =>
  [...(report.value?.agents || [])].sort((a, b) => {
    const byStatus =
      AVAILABILITY_ORDER.indexOf(a.availability) -
      AVAILABILITY_ORDER.indexOf(b.availability)
    return byStatus || a.name.localeCompare(b.name)
  })
)

const statusFilter = ref('')

const filteredAgents = computed(() =>
  statusFilter.value
    ? sortedAgents.value.filter(agent => agent.availability === statusFilter.value)
    : sortedAgents.value
)

// How many people are in each status: supervisors were counting rows by hand
const availabilityCounts = computed(() =>
  (report.value?.agents || []).reduce((counts, agent) => {
    counts[agent.availability] = (counts[agent.availability] || 0) + 1
    return counts
  }, {})
)

const AVAILABILITY_META = {
  online: { labelKey: 'AVAILABILITY.ONLINE', dotClass: 'bg-n-teal-9' },
  busy: { labelKey: 'AVAILABILITY.BUSY', dotClass: 'bg-n-amber-9' },
  offline: { labelKey: 'AVAILABILITY.OFFLINE', dotClass: 'bg-n-slate-9' },
}

const availabilityOptions = computed(() =>
  Object.entries(AVAILABILITY_META).map(([value, meta]) => ({
    value,
    label: t(meta.labelKey),
    dotClass: meta.dotClass,
  }))
)

const noticeAgent = ref(null)
const noticeText = ref('')
const sendingNotice = ref(false)

const openNotice = agent => {
  noticeAgent.value = agent
  noticeText.value = ''
}

const submitNotice = async () => {
  const content = noticeText.value.trim()
  if (!content || sendingNotice.value) return

  sendingNotice.value = true
  try {
    await sendAgentNotice(accountId.value, {
      user_id: noticeAgent.value.id,
      content,
    })
    noticeAgent.value = null
    useAlert(t('NOTICE.SENT'))
  } catch (error) {
    useAlert(t('NOTICE.ERROR'))
  } finally {
    sendingNotice.value = false
  }
}

const lostAgent = ref(null)
const lostThreads = ref([])
const loadingLost = ref(false)

const openLostThreads = async agent => {
  lostAgent.value = agent
  lostThreads.value = []
  loadingLost.value = true
  try {
    const { data } = await getLostThreads(accountId.value, {
      user_id: agent.id,
      since: filterSince.value || undefined,
      until: filterUntil.value || undefined,
    })
    lostThreads.value = data
  } catch (error) {
    useAlert(t('MONITORING.AGENTS.LOST_ERROR'))
    lostAgent.value = null
  } finally {
    loadingLost.value = false
  }
}

// Supervisor jumps from the table straight to what the operator handled today
const openAgentThreads = agentId => {
  const today = new Date().toLocaleDateString('sv')
  router.push({ path: '/', query: { agent_id: agentId, since: today, until: today } })
}

const openConversation = async id => {
  store.dispatch('conversations/select', id)
  store.dispatch('conversations/fetchMessages', id)
  router.push('/')
}

// The profile arrives asynchronously: on a hard reload the account id is not
// known yet when the page mounts, so the first load waits for it instead
watch(
  accountId,
  id => {
    if (!id) return
    loadFilterOptions()
    loadReport()
  },
  { immediate: true }
)

onMounted(() => {
  refreshTimer = setInterval(loadReport, REFRESH_INTERVAL)
})

onUnmounted(() => {
  if (refreshTimer) clearInterval(refreshTimer)
})
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-y-auto bg-n-background">
    <header class="flex items-center justify-between px-6 h-14 shrink-0">
      <h1 class="text-heading-1 text-n-slate-12">
        {{ $t('MONITORING.TITLE') }}
      </h1>
      <div class="flex items-center gap-3">
        <span v-if="loading" class="text-xs text-n-slate-10">
          {{ $t('MONITORING.REFRESHING') }}
        </span>
        <span v-else-if="updatedAt" class="text-xs text-n-slate-10">
          {{
            $t('MONITORING.UPDATED_AT', {
              time: updatedFormatter.format(updatedAt),
            })
          }}
        </span>
        <Button
          v-tooltip.bottom="$t('MONITORING.REFRESH')"
          slate
          ghost
          sm
          icon="i-lucide-refresh-cw"
          :disabled="loading"
          @click="loadReport"
        />
      </div>
    </header>

    <div class="flex flex-col gap-6 px-6 pb-8">
      <!-- Filters -->
      <div class="flex flex-wrap items-center gap-2">
        <Select
          v-model="filterInboxId"
          :options="inboxOptions"
          :placeholder="$t('MONITORING.FILTERS.ANY_INBOX')"
        />
        <Select
          v-model="filterAgentId"
          :options="agentOptions"
          :placeholder="$t('MONITORING.FILTERS.ANY_AGENT')"
        />
        <Select
          v-model="filterTagId"
          :options="tagOptions"
          :placeholder="$t('MONITORING.FILTERS.ANY_TAG')"
        />
        <input
          v-model="filterSince"
          type="date"
          class="h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
        />
        <span class="text-n-slate-10">—</span>
        <input
          v-model="filterUntil"
          type="date"
          class="h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
        />
      </div>

      <!-- Thread state counters -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div
          v-for="counter in counters"
          :key="counter.key"
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <span
            class="flex items-center gap-1.5 text-xs"
            :class="counter.alert ? 'text-n-ruby-11' : 'text-n-slate-11'"
          >
            <span :class="[counter.icon, 'size-3.5']" />
            {{ $t(`MONITORING.COUNTERS.${counter.key}`) }}
          </span>
          <span
            class="text-2xl font-semibold"
            :class="counter.alert ? 'text-n-ruby-11' : 'text-n-slate-12'"
          >
            {{ counter.value }}
          </span>
        </div>
      </div>

      <!-- Response times -->
      <div v-if="report" class="flex flex-wrap gap-3">
        <div
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1 min-w-44"
        >
          <span class="text-xs text-n-slate-11">
            {{ $t('MONITORING.RESPONSE.AVG') }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12">
            {{ formatDuration(report.response_stats.avg) }}
          </span>
        </div>
        <div
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1 min-w-44"
        >
          <span class="text-xs text-n-slate-11">
            {{ $t('MONITORING.RESPONSE.MAX') }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12">
            {{ formatDuration(report.response_stats.max) }}
          </span>
        </div>
        <div
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1 min-w-44"
        >
          <span class="text-xs text-n-slate-11">
            {{ $t('MONITORING.RESPONSE.COUNT') }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12">
            {{ report.response_stats.count }}
          </span>
        </div>
      </div>

      <!-- Stuck threads -->
      <section v-if="report?.stuck?.length" class="flex flex-col gap-2">
        <h2 class="m-0 text-heading-2 text-n-ruby-11">
          {{ $t('MONITORING.STUCK.TITLE') }}
        </h2>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
          <button
            v-for="thread in report.stuck"
            :key="thread.id"
            type="button"
            class="flex items-center w-full gap-3 px-4 py-2.5 text-left border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            @click="openConversation(thread.id)"
          >
            <span class="text-heading-3 text-n-slate-12">
              №{{ thread.display_id }}
            </span>
            <span class="flex-1 truncate text-body-main text-n-slate-11">
              {{ thread.contact_name || $t('CONVERSATIONS.UNKNOWN') }}
              <template v-if="thread.assignee_name">
                · {{ thread.assignee_name }}
              </template>
            </span>
            <span class="shrink-0 text-xs text-n-ruby-11">
              {{ $t('MONITORING.STUCK.WAITING_SINCE') }}
              {{ formatWaitingSince(thread.waiting_since) }}
            </span>
          </button>
        </div>
      </section>

      <!-- Agents state -->
      <section v-if="report" class="flex flex-col gap-2">
        <h2 class="m-0 text-heading-2 text-n-slate-12">
          {{ $t('MONITORING.AGENTS.TITLE') }}
        </h2>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="px-3 py-1 text-xs border-0 rounded-full cursor-pointer"
            :class="
              statusFilter ? 'bg-n-alpha-2 text-n-slate-11' : 'bg-n-brand text-white'
            "
            @click="statusFilter = ''"
          >
            {{ $t('MONITORING.AGENTS.ALL', { count: report.agents.length }) }}
          </button>
          <button
            v-for="option in availabilityOptions"
            :key="option.value"
            type="button"
            class="flex items-center gap-1.5 px-3 py-1 text-xs border-0 rounded-full cursor-pointer"
            :class="
              statusFilter === option.value
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
            @click="statusFilter = statusFilter === option.value ? '' : option.value"
          >
            <span class="rounded-full size-2" :class="option.dotClass" />
            {{ option.label }}
            {{ availabilityCounts[option.value] || 0 }}
          </button>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-auto max-h-[70vh]">
          <table class="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr class="text-left text-xs text-n-slate-11">
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.NAME') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.STATUS') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.TIME_IN_STATUS') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                />
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.ACTIVE') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.SLOTS') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.AVG_RESPONSE') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.WAITING_CLIENT') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.WAITING_AGENT') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.REPLIED') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.LOST') }}
                </th>
                <th
                  class="sticky top-0 z-10 px-4 py-2 font-medium bg-n-solid-1 shadow-[inset_0_-1px_0_0_rgb(var(--n-weak))]"
                >
                  {{ $t('MONITORING.AGENTS.SHIFT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="agent in filteredAgents"
                :key="agent.id"
                v-tooltip.top="$t('MONITORING.AGENTS.OPEN_THREADS')"
                class="cursor-pointer [&>td]:border-t [&>td]:border-n-weak hover:bg-n-alpha-1"
                @click="openAgentThreads(agent.id)"
              >
                <td class="px-4 py-2">
                  <span class="flex items-center gap-2 text-n-slate-12">
                    <Avatar :name="agent.name" :size="24" :src="agent.avatar_url" logo-fallback />
                    {{ agent.name }}
                  </span>
                </td>
                <td class="px-4 py-2" @click.stop>
                  <span
                    v-if="agent.id === currentUser?.id"
                    class="flex items-center gap-1.5 px-2 text-n-slate-12"
                  >
                    <span
                      class="size-2 rounded-full"
                      :class="AVAILABILITY_META[agent.availability]?.dotClass"
                    />
                    {{ $t(AVAILABILITY_META[agent.availability]?.labelKey) }}
                  </span>
                  <Select
                    v-else
                    :model-value="agent.availability"
                    :options="availabilityOptions"
                    @update:model-value="
                      value => changeAgentAvailability(agent, value)
                    "
                  />
                </td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ durationSince(agent.availability_changed_at) }}
                </td>
                <td class="px-4 py-2" @click.stop>
                  <Button
                    v-if="agent.id !== currentUser?.id"
                    slate
                    faded
                    xs
                    icon="i-lucide-megaphone"
                    :aria-label="$t('NOTICE.SEND')"
                    @click="openNotice(agent)"
                  />
                </td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ agent.active_count }}
                </td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ agent.slots_free ?? '∞' }}
                </td>
                <td class="px-4 py-2 text-n-slate-12 whitespace-nowrap">
                  {{ formatDuration(agent.avg_response_first) }} /
                  {{ formatDuration(agent.avg_response_next) }}
                </td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ agent.waiting_client_count }}
                </td>
                <td
                  class="px-4 py-2"
                  :class="
                    agent.waiting_agent_count > 0
                      ? 'text-n-ruby-11 font-medium'
                      : 'text-n-slate-12'
                  "
                >
                  {{ agent.waiting_agent_count }}
                </td>
                <td class="px-4 py-2 text-n-slate-12">
                  {{ agent.replied_count }}
                </td>
                <td class="px-4 py-2 text-n-slate-12" @click.stop>
                  <button
                    v-if="agent.lost_count"
                    type="button"
                    class="p-0 bg-transparent border-0 cursor-pointer text-n-brand hover:underline"
                    @click="openLostThreads(agent)"
                  >
                    {{ agent.lost_count }}
                  </button>
                  <span v-else>{{ agent.lost_count }}</span>
                </td>
                <td class="px-4 py-2 text-n-slate-11">
                  {{ durationSince(agent.shift_started_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </section>
    </div>

    <Modal
      :show="Boolean(noticeAgent)"
      :title="$t('NOTICE.TITLE', { name: noticeAgent?.name })"
      @close="noticeAgent = null"
    >
      <form class="flex flex-col gap-5" @submit.prevent="submitNotice">
        <textarea
          v-model="noticeText"
          rows="3"
          :placeholder="$t('NOTICE.PLACEHOLDER')"
          class="block w-full px-3 py-2.5 text-sm border-none rounded-xl resize-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
        />
        <div class="flex justify-end">
          <Button
            type="submit"
            :label="$t('NOTICE.SUBMIT')"
            :disabled="!noticeText.trim() || sendingNotice"
          />
        </div>
      </form>
    </Modal>

    <Modal
      :show="Boolean(lostAgent)"
      :title="$t('MONITORING.AGENTS.LOST_TITLE', { name: lostAgent?.name })"
      :description="$t('MONITORING.AGENTS.LOST_DESC')"
      @close="lostAgent = null"
    >
      <p v-if="loadingLost" class="py-4 text-body-main text-n-slate-11">
        {{ $t('MONITORING.REFRESHING') }}
      </p>
      <p v-else-if="!lostThreads.length" class="py-4 text-body-main text-n-slate-11">
        {{ $t('MONITORING.AGENTS.LOST_EMPTY') }}
      </p>
      <div v-else class="flex flex-col overflow-y-auto max-h-96">
        <button
          v-for="thread in lostThreads"
          :key="`${thread.id}-${thread.lost_at}`"
          type="button"
          class="flex items-center w-full gap-3 px-2 py-2.5 text-left bg-transparent border-0 border-b cursor-pointer border-n-weak last:border-b-0 hover:bg-n-alpha-1"
          @click="openConversation(thread.id)"
        >
          <span class="text-heading-3 text-n-slate-12">
            №{{ thread.display_id }}
          </span>
          <span class="flex-1 truncate text-body-main text-n-slate-11">
            {{ thread.contact_name || $t('CONVERSATIONS.UNKNOWN') }}
            · {{ thread.inbox_name }}
          </span>
          <span class="shrink-0 text-xs text-n-slate-10">
            {{ formatWaitingSince(thread.lost_at) }}
          </span>
        </button>
      </div>
    </Modal>
  </div>
</template>
